import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IcWifi } from '../theme/icons';
import useServerOnline from './useServerOnline';
import { subscribe, startSaleSync } from './saleQueue';
import { refreshProductCache } from './productCache';
import { useEngine } from '../desktop/engine';

/*
 * Footer connectivity strip: a WiFi icon plus a grey status line.
 *   grey wifi  + "Connecté à Internet"                         -> online, idle
 *   red  wifi  + "Hors ligne — les ventes seront enregistrées  -> no internet
 *                sur l'appareil"
 *   grey wifi  + "Synchronisation…" / "N vente(s) en attente"  -> catching up
 *   red  text  + "N vente(s) non synchronisée(s)"              -> server refused some
 *
 * The sentence is grey (as requested) even when offline; the red WiFi icon is
 * what signals the outage. Mounting this also boots the offline engine (sale
 * sync + product-catalog warm), so it lives wherever the app renders the footer.
 * Icon and sentence share one source of truth (useServerOnline) so they agree.
 *
 * Desktop app: the local engine owns the queue (every write, not only sales),
 * so the strip reflects the engine's link with the VPS instead and links to
 * the « Ce poste » page.
 */
export default function ConnectivityFooter({ className = '' }) {
  const engine = useEngine();
  if (engine.available === true) return <EngineStrip status={engine.status} unreachable={engine.unreachable} className={className} />;
  if (engine.available === null) return null; // first answer pending: nothing to say yet
  return <BrowserStrip className={className} />;
}

function EngineStrip({ status, unreachable, className }) {
  if (unreachable) {
    return (
      <div role="status" aria-live="polite" className={`flex items-center justify-center gap-2 text-sm ${className}`}>
        <IcWifi className="text-red-500" />
        <span className="text-red-500">Moteur local injoignable — redémarrez l'application</span>
      </div>
    );
  }
  if (!status) return null;
  const online = status.upstream_online === true;
  const queued = status.pending + status.blocked;
  const direct = status.bootstrapped && status.serving === 'server';
  const idle = direct && queued === 0 && status.conflicts === 0;
  if (idle) return null; // connected, working directly on the server: nothing to say

  let text;
  let danger = false;
  if (!status.bootstrapped) {
    text = status.has_token
      ? `Première synchronisation… ${status.bootstrap_progress || ''}`
      : 'Première synchronisation : connectez-vous avec Internet';
  } else if (!online) {
    const ago = status.offline_since ? sinceText(status.offline_since) : '';
    text = `Hors ligne${ago ? ` depuis ${ago}` : ''} — le poste travaille en local, ${queued} opération(s) à envoyer`;
  } else if (queued > 0) {
    text = `Connexion rétablie — envoi de ${queued} opération(s) au serveur…`;
  } else if (status.conflicts > 0) {
    text = `${status.conflicts} opération(s) en conflit — à vérifier`;
    danger = true;
  } else {
    text = 'Synchronisation en cours…';
  }

  return (
    <Link to="/poste" role="status" aria-live="polite" className={`flex items-center justify-center gap-2 text-sm hover:underline ${className}`}>
      <IcWifi className={online ? 'text-gray-400' : 'text-red-500'} />
      <span className={danger ? 'text-red-500' : 'text-gray-400'}>{text}</span>
    </Link>
  );
}

function sinceText(iso) {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 48) return `${h} h`;
  return `${Math.floor(h / 24)} j`;
}

function BrowserStrip({ className }) {
  const online = useServerOnline();
  const [q, setQ] = useState({ pending: 0, errors: 0, syncing: false });

  useEffect(() => {
    startSaleSync();
    // Après le premier écran : la copie hors ligne ne doit pas concurrencer
    // les requêtes de la page qui s'ouvre (perf 2026-10-05).
    const warm = setTimeout(() => refreshProductCache(), 6000);
    const onOnline = () => refreshProductCache(true);
    window.addEventListener('online', onOnline);
    const unsub = subscribe((s) => setQ({ pending: s.pending, errors: s.errors, syncing: s.syncing }));
    return () => { clearTimeout(warm); window.removeEventListener('online', onOnline); unsub(); };
  }, []);

  // Sélection Studio (2026-08-17) : la ligne « Connecté à Internet » + icône
  // est retirée quand tout va bien. Le composant reste monté (il démarre le
  // moteur offline) et la bande réapparaît hors ligne / pendant la synchro.
  const idle = online && !q.syncing && q.errors === 0 && q.pending === 0;

  let text = 'Connecté à Internet';
  let danger = false;
  if (!online) {
    text = "Hors ligne — les ventes seront enregistrées sur l'appareil";
  } else if (q.syncing) {
    text = 'Synchronisation en cours…';
  } else if (q.errors > 0) {
    text = `${q.errors} vente(s) non synchronisée(s)`;
    danger = true;
  } else if (q.pending > 0) {
    text = `${q.pending} vente(s) en attente de synchronisation`;
  }

  if (idle) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-center justify-center gap-2 text-sm ${className}`}
    >
      <IcWifi className={online ? 'text-gray-400' : 'text-red-500'} />
      <span className={danger ? 'text-red-500' : 'text-gray-400'}>{text}</span>
    </div>
  );
}
